import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronRight, mdiInformationOutline } from '@mdi/js';
import './ClassesHelpModal.css';

/**
 * Page-scoped help for the Reconcile Payments page. Broad finances topics (Stripe setup,
 * payout country, platform fee, payouts, refunds overview) live on the Manage Finances portal
 * help modal; this one covers only what is specific to reconciling external payments.
 */
const ReconcileHelpModal = ({ isOpen, onClose }) => {
    const [expandedSections, setExpandedSections] = useState([]);
    const sectionRefs = useRef({});

    useEffect(() => {
        if (!isOpen) setExpandedSections([]);
    }, [isOpen]);

    const toggleSection = (sectionId) => {
        const isExpanding = !expandedSections.includes(sectionId);
        setExpandedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
        if (isExpanding) {
            setTimeout(() => {
                sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 0);
        }
    };

    if (!isOpen) return null;

    const sections = [
        {
            id: 'what-is-reconcile',
            title: '1. What "reconcile" means',
            content: (
                <>
                    <p>An external payment (cash, EFTPOS, or bank transfer) is counted as <strong>paid</strong> the moment you record it. <strong>Reconciling</strong> is a separate, optional step you do at your own pace: ticking off each payment against your own records (your bank statement or till), so your books match.</p>
                    <p>Reconciling never changes whether something is paid. Card payments taken through Stripe reconcile automatically, so they do not appear on this page.</p>
                </>
            )
        },
        {
            id: 'money-in-out',
            title: '2. Payments and refunds (money in and money out)',
            content: (
                <>
                    <p>This page shows both directions of external money:</p>
                    <ul>
                        <li><strong>Payments</strong> are money in.</li>
                        <li><strong>Refunds</strong> are money out, shown with the refund icon, a <strong>"Refund"</strong> tag, and a red amount with a minus sign.</li>
                    </ul>
                    <p>Each row's description tells you what the money was for. An <strong>"Adjustment"</strong> row is a flat charge you added yourself (via <strong>Add charge</strong> on a student's Payments tab), such as a drop-in fee or school merchandise, rather than a class enrolment.</p>
                    <p>Showing both means the page reflects what you have actually taken in externally, not just what came in.</p>
                </>
            )
        },
        {
            id: 'net-total',
            title: '3. The net total',
            content: (
                <>
                    <p>The <strong>Net external</strong> line is your payments minus your refunds. It is shown <strong>per currency</strong> (one line for each currency), and is never converted or added across currencies. It is your true external position after refunds.</p>
                </>
            )
        },
        {
            id: 'filters',
            title: '4. Searching and filtering',
            content: (
                <>
                    <p>Narrow the list with:</p>
                    <ul>
                        <li><strong>Search</strong> by student name, reference code, or amount.</li>
                        <li><strong>Method</strong> (bank transfer, EFTPOS, cash, or other).</li>
                        <li><strong>Reconciled state</strong> (all, reconciled, or unreconciled).</li>
                        <li><strong>Money direction</strong> (all money, payments only, or refunds only).</li>
                    </ul>
                    <p>The net total reflects the method, date, and reconciled filters, but always covers both payments and refunds so the figure stays meaningful.</p>
                </>
            )
        },
        {
            id: 'reconciling',
            title: '5. Reconciling a row',
            content: (
                <>
                    <p>On any unreconciled row, click <strong>Reconcile</strong>. You can correct the payment method and add an optional match note (for example a bank reference or "till, 14 Jun"), then choose <strong>Mark reconciled</strong>.</p>
                    <p>Reconciled rows stay on the page with a blue <strong>Reconciled</strong> badge. Reconciling only ticks the row off; it never changes the amount.</p>
                </>
            )
        }
    ];

    return (
        <div className="classes-help-overlay" onClick={onClose}>
            <div className="classes-help-modal" onClick={e => e.stopPropagation()}>
                <div className="classes-help-header">
                    <div className="classes-help-header-content">
                        <Icon path={mdiInformationOutline} size={1.2} color="var(--dl-color-purple-default)" />
                        <h2>Reconcile Payments Help</h2>
                    </div>
                    <button className="classes-help-close" onClick={onClose}>×</button>
                </div>

                <div className="classes-help-body">
                    {sections.map(section => (
                        <div key={section.id} className="help-accordion-section" ref={el => sectionRefs.current[section.id] = el}>
                            <button
                                className="help-accordion-header"
                                onClick={() => toggleSection(section.id)}
                            >
                                <Icon
                                    path={expandedSections.includes(section.id) ? mdiChevronDown : mdiChevronRight}
                                    size={0.8}
                                    color="var(--dl-color-purple-default)"
                                />
                                <span>{section.title}</span>
                            </button>
                            {expandedSections.includes(section.id) && (
                                <div className="help-accordion-content">
                                    {section.content}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="classes-help-footer">
                    <button className="classes-help-btn-secondary" onClick={onClose}>Got it!</button>
                </div>
            </div>
        </div>
    );
};

export default ReconcileHelpModal;
